import React, { useState, useEffect, useMemo } from 'react';
import { notify } from './Notifications';
import { VegaEmbed } from 'react-vega';
import DataTable, { TableColumn, createTheme } from 'react-data-table-component';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import ActivityStatus from './ChatMessage/ActivityStatus';
import CollapsibleCard from './ChatMessage/CollapsibleCard';
import {
  ChartIcon, CheckIcon, CloseIcon, DownloadIcon, ExpandIcon, SparklesIcon, SpinnerIcon, TableIcon, WrenchIcon,
} from './icons';

// Component for rendering Snowflake thinking/reasoning
export const SnowflakeThinking: React.FC<{ text: string }> = ({ text }) => {
  const { colors } = useTheme();

  if (!text) return null;

  return (
    <CollapsibleCard icon={<SparklesIcon size={16} />} title="Reasoning process">
      <div style={{
        padding: '12px 14px',
        fontSize: '13px',
        color: colors.textSecondary,
        lineHeight: '1.6',
        whiteSpace: 'pre-wrap',
      }}>
        {text}
      </div>
    </CollapsibleCard>
  );
};

// Component for rendering Snowflake charts using Vega-Lite
export const SnowflakeChart: React.FC<{ spec: any }> = ({ spec }) => {
  if (!spec) return null;

  return (
    <CollapsibleCard icon={<ChartIcon size={16} />} title="Data visualization" defaultExpanded>
      <div style={{ padding: '16px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <VegaEmbed
          spec={spec}
          options={{
            mode: 'vega-lite',
            actions: false,
            renderer: 'svg'
          }}
        />
      </div>
    </CollapsibleCard>
  );
};

// Component for rendering Snowflake tables
export const SnowflakeTable: React.FC<{ data: any; agentId?: string }> = ({ data: tableData, agentId }) => {
  const { colors } = useTheme();
  const { getAccessToken } = useAuth();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isLoadingPage, setIsLoadingPage] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [paginatedData, setPaginatedData] = useState<any[]>([]);
  const [totalRows, setTotalRows] = useState(0);
  const abortControllerRef = React.useRef<AbortController | null>(null);
  const pageCacheRef = React.useRef<Map<string, any>>(new Map());

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const fetchPage = async (page: number, perPage: number) => {
    const statementHandle = tableData?.statement_handle;
    if (!statementHandle) {
      return;
    }

    // Check cache first
    const cacheKey = `${statementHandle}-${page}-${perPage}`;
    const cachedPage = pageCacheRef.current.get(cacheKey);

    if (cachedPage) {
      setPaginatedData(cachedPage.formattedRows);
      setTotalRows(cachedPage.totalRows);
      setCurrentPage(page);
      setPageSize(perPage);
      return;
    }

    setIsLoadingPage(true);

    try {
      const accessToken = await getAccessToken();
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
      const agentParam = agentId ? `&agent_id=${encodeURIComponent(agentId)}` : '';
      const apiUrl = `${apiBaseUrl}/api/agents/query-results-page/${statementHandle}?page=${page}&page_size=${perPage}${agentParam}`;

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });

      if (!response.ok) {
        if (response.status === 410) {
          setError('Query results have expired (24h limit). Query needs to be executed again.');
        } else {
          setError('Failed to load page data');
        }
        return;
      }

      const result = await response.json();

      // Convert data rows to objects for DataTable
      const rowType = result.metadata?.rowType || [];
      const formattedRows = result.data.map((row: any[], rowIdx: number) => {
        const rowObj: any = { id: (page - 1) * perPage + rowIdx };
        row.forEach((cell, cellIdx) => {
          rowObj[`col_${cellIdx}`] = cell;
        });
        return rowObj;
      });

      // Cache the page
      pageCacheRef.current.set(cacheKey, {
        formattedRows,
        totalRows: result.total_rows
      });

      setPaginatedData(formattedRows);
      setTotalRows(result.total_rows);
      setCurrentPage(page);
      setPageSize(perPage);
    } catch (error) {
      console.error('Error fetching page:', error);
      setError('Failed to load data');
    } finally {
      setIsLoadingPage(false);
    }
  };

  useEffect(() => {
    if (tableData?.statement_handle && tableData?.total_rows > (tableData?.preview_rows || 0)) {
      fetchPage(1, pageSize);
    }
  }, [tableData?.statement_handle]);

  const customStyles = useMemo(() => ({
    table: {
      style: {
        backgroundColor: colors.surface,
      },
    },
    headRow: {
      style: {
        backgroundColor: colors.surfaceElevated,
        borderBottomWidth: '2px',
        borderBottomColor: colors.border,
        borderBottomStyle: 'solid' as const,
        minHeight: '40px',
      },
    },
    headCells: {
      style: {
        color: colors.textPrimary,
        fontWeight: 600,
        fontSize: '13px',
        paddingLeft: '12px',
        paddingRight: '12px',
      },
    },
    rows: {
      style: {
        backgroundColor: colors.surface,
        borderBottomColor: colors.borderLight,
        minHeight: '44px',
      },
      highlightOnHoverStyle: {
        backgroundColor: colors.surfaceElevated,
        borderBottomColor: colors.border,
        outline: 'none',
      },
    },
    cells: {
      style: {
        color: colors.textPrimary,
        fontSize: '13px',
        paddingLeft: '12px',
        paddingRight: '12px',
        paddingTop: '8px',
        paddingBottom: '8px',
      },
    },
    pagination: {
      style: {
        backgroundColor: colors.surface,
        color: colors.textPrimary,
        borderTopColor: colors.border,
      },
      pageButtonsStyle: {
        color: colors.textPrimary,
        fill: colors.textPrimary,
        '&:disabled': {
          color: colors.textMuted,
          fill: colors.textMuted,
        },
      },
    },
    noData: {
      style: {
        backgroundColor: colors.surface,
        color: colors.textMuted,
      },
    },
  }), [colors]);

  const renderDataTable = (paginationPerPage: number = 10) => {
    const useServerPagination = tableData?.statement_handle && tableData?.total_rows > (tableData?.preview_rows || 0);
    const effectivePageSize = useServerPagination ? pageSize : paginationPerPage;

    return (
      <DataTable
        columns={columns}
        data={useServerPagination ? paginatedData : rows}
        customStyles={customStyles}
        pagination
        paginationServer={useServerPagination}
        paginationTotalRows={useServerPagination ? totalRows : rows.length}
        paginationDefaultPage={currentPage}
        paginationPerPage={effectivePageSize}
        paginationRowsPerPageOptions={[10, 25, 50, 100]}
        onChangePage={(page) => {
          if (useServerPagination) {
            fetchPage(page, pageSize);
          }
        }}
        onChangeRowsPerPage={(newPerPage, page) => {
          if (useServerPagination) {
            fetchPage(page, newPerPage);
          }
        }}
        progressPending={isLoadingPage}
        progressComponent={
          <div style={{ padding: '24px' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
              <SpinnerIcon size={18} />
              Loading…
            </span>
          </div>
        }
        dense
        highlightOnHover
        responsive
        noDataComponent={
          <div style={{ padding: '24px', color: colors.textMuted }}>
            {error || 'No data available'}
          </div>
        }
      />
    );
  };

  const downloadCSV = async () => {
    const statementHandle = tableData?.statement_handle;
    if (!statementHandle) {
      console.error('No statement handle available for download');
      return;
    }

    if (isDownloading) {
      return;
    }

    setIsDownloading(true);
    abortControllerRef.current = new AbortController();

    try {
      const accessToken = await getAccessToken();
      const title = encodeURIComponent(tableData.title || 'query_results');
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
      const agentParam = agentId ? `&agent_id=${encodeURIComponent(agentId)}` : '';
      const apiUrl = `${apiBaseUrl}/api/agents/download-query-results/${statementHandle}?title=${title}${agentParam}`;

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${accessToken}`
        },
        signal: abortControllerRef.current.signal
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Failed to download CSV:', response.status, errorText);

        if (response.status === 410) {
          notify('Query results have expired. Snowflake keeps results for 24 hours only. Query needs to be executed again for fresh results.', 'warning');
        } else {
          notify(`Failed to download CSV: ${response.status}`, 'error');
        }
        return;
      }

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;

      const filename = `${tableData.title || 'query_results'}_${new Date().toISOString().split('T')[0]}.csv`;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch (error: any) {
      if (error.name === 'AbortError') {
        console.log('Download cancelled');
      } else {
        console.error('Error downloading CSV:', error);
      }
    } finally {
      setIsDownloading(false);
      abortControllerRef.current = null;
    }
  };

  const formatCellValue = (value: any): React.ReactNode => {
    if (value === null || value === undefined) {
      return <span style={{ color: colors.textMuted, fontStyle: 'italic' }}>null</span>;
    }

    if (typeof value === 'object') {
      return (
        <pre style={{
          margin: 0,
          fontSize: '11px',
          backgroundColor: colors.surfaceElevated,
          padding: '6px 8px',
          borderRadius: '4px',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          maxHeight: '100px',
          overflow: 'auto',
          maxWidth: '300px'
        }}>
          {JSON.stringify(value, null, 2)}
        </pre>
      );
    }

    const strValue = String(value);
    if (strValue.startsWith('{') || strValue.startsWith('[')) {
      try {
        const parsed = JSON.parse(strValue);
        return (
          <pre style={{
            margin: 0,
            fontSize: '11px',
            backgroundColor: colors.surfaceElevated,
            padding: '6px 8px',
            borderRadius: '4px',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            maxHeight: '100px',
            overflow: 'auto',
            maxWidth: '300px'
          }}>
            {JSON.stringify(parsed, null, 2)}
          </pre>
        );
      } catch {
        return strValue;
      }
    }

    return strValue;
  };

  const { columns, rows } = useMemo(() => {
    if (!tableData?.result_set?.data) {
      return { columns: [], rows: [] };
    }

    const { result_set } = tableData;
    const { data } = result_set;
    const rowType = result_set.resultSetMetaData?.rowType || result_set.rowType || result_set.row_type || [];

    const cols: TableColumn<any>[] = rowType.map((col: any, idx: number) => ({
      name: (
        <div>
          {col.name || col.column_name || col.columnName || `Column ${idx + 1}`}
          {(col.type || col.data_type || col.dataType) && (
            <span style={{ fontSize: '10px', color: colors.textMuted, marginLeft: '4px' }}>
              ({col.type || col.data_type || col.dataType})
            </span>
          )}
        </div>
      ),
      selector: (row: any) => row[`col_${idx}`],
      cell: (row: any) => formatCellValue(row[`col_${idx}`]),
      sortable: true,
      wrap: true,
      width: '150px',
    }));

    if (cols.length === 0 && data.length > 0) {
      const firstRow = data[0];
      for (let i = 0; i < firstRow.length; i++) {
        cols.push({
          name: `Column ${i + 1}`,
          selector: (row: any) => row[`col_${i}`],
          cell: (row: any) => formatCellValue(row[`col_${i}`]),
          sortable: true,
          wrap: true,
          width: '150px',
        });
      }
    }

    const tableRows = data.map((row: any[], rowIdx: number) => {
      const rowObj: any = { id: rowIdx };
      row.forEach((cell, cellIdx) => {
        rowObj[`col_${cellIdx}`] = cell;
      });
      return rowObj;
    });

    return { columns: cols, rows: tableRows };
  }, [tableData, colors.textMuted]);

  if (!tableData && !error) return null;

  return (
    <>
      <CollapsibleCard
        icon={<TableIcon size={16} />}
        title="Query results"
        defaultExpanded
        badges={tableData && (
          <span className="cv-badge">
            {tableData.total_rows ? (
              <>
                {tableData.total_rows.toLocaleString()} {tableData.total_rows === 1 ? 'row' : 'rows'}
                {tableData.preview_rows && tableData.preview_rows < tableData.total_rows && (
                  <span style={{ fontSize: '11px', marginLeft: '4px' }}>
                    (showing {tableData.preview_rows})
                  </span>
                )}
              </>
            ) : (
              <>
                {tableData.result_set?.data?.length || 0} {(tableData.result_set?.data?.length || 0) === 1 ? 'row' : 'rows'}
              </>
            )}
          </span>
        )}
        actions={<>
          <button type="button" className="cv-tool-btn" onClick={downloadCSV} disabled={isDownloading}>
            {isDownloading ? <SpinnerIcon size={14} /> : <DownloadIcon size={14} />}
            {isDownloading ? 'Downloading…' : 'CSV'}
          </button>
          <button type="button" className="cv-tool-btn" onClick={() => setIsModalOpen(true)}>
            <ExpandIcon size={14} />
            Expand
          </button>
        </>}
      >
        <div>
            {error ? (
              <div style={{ padding: '16px', color: colors.errorHover, fontSize: '13px' }}>{error}</div>
            ) : tableData?.title ? (
              <div style={{
                padding: '12px 16px 0',
                fontSize: '14px',
                fontWeight: 600,
                color: colors.textPrimary
              }}>
                {tableData.title}
              </div>
            ) : null}
            {!error && renderDataTable(10)}
        </div>
      </CollapsibleCard>

      {isModalOpen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
          onClick={() => setIsModalOpen(false)}
        >
          <div
            style={{
              backgroundColor: colors.surface,
              borderRadius: '12px',
              width: '95vw',
              maxWidth: '1600px',
              maxHeight: '90vh',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              padding: '16px 20px',
              borderBottom: `1px solid ${colors.border}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              backgroundColor: colors.surfaceElevated,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ display: 'flex', color: colors.textSecondary }}><TableIcon size={18} /></span>
                <span style={{
                  fontWeight: 600,
                  fontSize: '16px',
                  color: colors.textPrimary
                }}>
                  {tableData?.title || 'Query Results'}
                </span>
                {tableData && (
                  <span style={{
                    fontSize: '13px',
                    color: colors.textMuted,
                    backgroundColor: colors.surface,
                    padding: '4px 10px',
                    borderRadius: '12px'
                  }}>
                    {tableData.total_rows ? (
                      <>
                        {tableData.total_rows.toLocaleString()} {tableData.total_rows === 1 ? 'row' : 'rows'}
                        {tableData.preview_rows && tableData.preview_rows < tableData.total_rows && (
                          <span style={{ fontSize: '11px', marginLeft: '4px' }}>
                            (showing {tableData.preview_rows})
                          </span>
                        )}
                      </>
                    ) : (
                      <>
                        {tableData.result_set?.data?.length || 0} {(tableData.result_set?.data?.length || 0) === 1 ? 'row' : 'rows'}
                      </>
                    )}
                  </span>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <button
                  onClick={downloadCSV}
                  disabled={isDownloading}
                  style={{
                    padding: '6px 12px',
                    fontSize: '14px',
                    backgroundColor: colors.primary,
                    color: '#fff',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: isDownloading ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    opacity: isDownloading ? 0.6 : 1,
                  }}
                >
                  {isDownloading ? <SpinnerIcon size={14} /> : <DownloadIcon size={14} />}
                  {isDownloading ? 'Downloading…' : 'Download CSV'}
                </button>
                <button
                  onClick={() => setIsModalOpen(false)}
                  style={{
                    padding: '6px 12px',
                    fontSize: '14px',
                    backgroundColor: 'transparent',
                    color: colors.textPrimary,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '6px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                  }}
                >
                  <CloseIcon size={14} />
                  Close
                </button>
              </div>
            </div>
            <div style={{
              flex: 1,
              overflow: 'auto',
              padding: '0',
            }}>
              {renderDataTable(25)}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Component for rendering Snowflake status updates
export const SnowflakeStatus: React.FC<{ message: string; active?: boolean }> = ({ message, active = false }) => {
  if (!message) return null;

  // Finished steps stay visible as history, so they get a check rather than a spinner.
  return <ActivityStatus icon={active ? undefined : <CheckIcon size={14} />} label={message} active={active} />;
};

// Component for rendering Snowflake tool status
export const SnowflakeToolStatus: React.FC<{ message: string; active?: boolean }> = ({ message, active = false }) => {
  if (!message) return null;

  return <ActivityStatus icon={<WrenchIcon size={14} />} label={message} active={active} />;
};
